import React, { useState, useRef, useEffect } from "react";
import {
  FaChevronDown,
  FaPlus,
  FaDownload,
  FaUserPlus,
  FaChevronUp,
} from "react-icons/fa";
import {
  ColumnDirective,
  ColumnsDirective,
  CommandColumn,
  GridComponent,
  Toolbar,
  Filter,
  Edit,
  Page,
  Sort,
  Inject,
  actionBegin,
  DetailRow,
  PdfExport,
} from "@syncfusion/ej2-react-grids";
import { UploaderComponent, Uploader } from "@syncfusion/ej2-react-inputs";
import { DialogComponent } from "@syncfusion/ej2-react-popups";
import {
  getFirestore,
  collection,
  query,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore"; // Import Firestore functions
import { onAuthStateChanged, getAuth } from "firebase/auth";
import { DataManager, Query } from "@syncfusion/ej2-data";
import assistanceLogService from "../../service/citizen/assistanceLogService";
import { getStorage, ref, deleteObject } from "firebase/storage";
import DisasterMgtService from "../../service/citizen/DisasterMgtService";
import disasterCategories from "../../disaster_categories.json";
import { ImageDialog } from "./AssistanceLog";
import { createElement } from "@syncfusion/ej2-base";
import jsPDF from "jspdf";
import "jspdf-autotable";
import blueSkyLogo from "../../../src/assets/bluesky-logo.svg";
import HeaderTitle from "../../components/HeaderTitle";

const sortSettings = {
  allowSorting: true,
};

const editing = {
  // allowDeleting: true,
  // allowAdding: true,
  allowEditing: true,
  showDeleteConfirmDialog: true,
  mode: "Dialog",
};

const filterSettings = {
  type: "Menu",
};

// Initialize Firebase Firestore
const db = getFirestore();
const auth = getAuth();

const RecoveryPlanDisasters = ({
  className = "",
  selectedDisasters,
  activeDisasters,
  caseStatus,
  caseNumber,
  femaNumber,
  disasterNumber,
}) => {
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const pdfExportRef = useRef(null);
  // State for Recovery Assistance Data
  const [recoveryAssistanceData, setRecoveryAssistanceData] = useState([]);
  const [userId, setUserId] = useState(null);
  const [data, setData] = useState([]);
  const selectedUserId = sessionStorage.getItem("userId");

  let elem;
  let uploadObj;
  let strm;
  let selectedRow;

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && selectedUserId) {
        setUserId(selectedUserId);
      } else {
        setData(null);
        console.log("No user is logged in");
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId, selectedDisasters, activeDisasters]);

  const fetchData = async () => {
    try {
      // Fetch user document
      const userDocRef = doc(db, "users", userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        setData({ id: userDoc.id, ...userDoc.data() });

        const general = await DisasterMgtService.fetchRecords(
          userId,
          "general"
        );
        const home = await DisasterMgtService.fetchRecords(
          userId,
          "home_repair"
        );
        const landscaping = await DisasterMgtService.fetchRecords(
          userId,
          "landscaping"
        );
        const vehicle = await DisasterMgtService.fetchRecords(
          userId,
          "vehicle"
        );
        const contents = await DisasterMgtService.fetchRecords(
          userId,
          "contents"
        );

        const filterBySelectedDisasters = (item) => {
          if (selectedDisasters.includes("All")) {
            return item.associatedDisasters.some((disaster) =>
              activeDisasters.includes(disaster)
            );
          }
          return item.associatedDisasters.some((disaster) =>
            selectedDisasters.includes(disaster)
          );
        };

        // Combine and transform the data for Recovery Plan
        const recoveryPlanData = [
          ...general.map((item) => {
            const donated = item.value || 0;
            const funding = item.funding || 500;
            const fundingAmount = donated + funding;
            const fundingGap = Math.abs(item.estimate - fundingAmount);

            return {
              ...item,
              RecoveryPlanID: item.generalID,
              category: "General",
              subcategory: item.sub_category,
              estimate: item.estimate,
              donated: donated,
              photoRecovery: item.photoGeneral,

              fundingGap: fundingGap,
            };
          }),
          ...home.map((item) => {
            const donated = item.value || 0;
            const funding = item.funding || 0;
            const fundingAmount = donated + funding;
            const fundingGap = Math.abs(item.estimate - fundingAmount);

            return {
              ...item,
              RecoveryPlanID: item.homeRepairID,
              category: "Home Repair and Replacement",
              subcategory: item.sub_category,
              estimate: item.estimate,
              donated: donated,
              // photoRecovery: item.photo_home_repair,
              funding: funding,
              fundingAmount: fundingAmount,
              fundingGap: fundingGap,
            };
          }),
        ];

        setRecoveryAssistanceData(
          recoveryPlanData.filter((item) => !item.is_deleted)
        );

        const generalData = general
          .filter(filterBySelectedDisasters)
          .map((item) => {
            const donated = item.value || 0;
            const fundingAmount = item.fundingAmount || 0;
            const fundingGap = fundingAmount + donated - item.estimate; // Corrected calculation

            return {
              ...item,
              generalID: item.generalID,
              category: "General",
              subcategory: item.sub_category,
              estimate: item.estimate,
              donated: donated,
              photoRecovery: item.photo_general,
              funding: item.funding,
              fundingAmount: fundingAmount,
              fundingGap: fundingGap,
              fundingGapColor: fundingGap < 0 ? "red" : "green",
            };
          });
        setGeneralData(generalData.filter((item) => !item.is_deleted));

        const homeRepairData = home
          .filter(filterBySelectedDisasters)
          .map((item) => {
            const donated = item.value || 0;
            const fundingAmount = item.fundingAmount || 0;
            const fundingGap = fundingAmount + donated - item.estimate; // Corrected calculation

            return {
              ...item,
              homeRepairID: item.homeRepairID,
              category: "Home Repair and Replacement",
              subcategory: item.sub_category,
              estimate: item.estimate,
              donated: donated,
              photoRecovery: item.photo_home_repair,
              funding: item.funding,
              fundingAmount: fundingAmount,
              fundingGap: fundingGap,
              fundingGapColor: fundingGap < 0 ? "red" : "green",
            };
          });
        setHomeRepairData(homeRepairData.filter((item) => !item.is_deleted));

        const landscapingData = landscaping
          .filter(filterBySelectedDisasters)
          .map((item) => {
            const donated = item.value || 0;
            const fundingAmount = item.fundingAmount || 0;
            const fundingGap = fundingAmount + donated - item.estimate; // Corrected calculation

            return {
              ...item,
              landscapingID: item.landscapingID,
              category: "Landscaping",
              subcategory: item.sub_category,
              estimate: item.estimate,
              donated: donated,
              photoRecovery: item.photo_landscaping,
              funding: item.funding,
              fundingAmount: fundingAmount,
              fundingGap: fundingGap,
              fundingGapColor: fundingGap < 0 ? "red" : "green",
            };
          });
        setLandscapingData(landscapingData.filter((item) => !item.is_deleted));

        const vehicleData = vehicle
          .filter(filterBySelectedDisasters)
          .map((item) => {
            const donated = item.value || 0;
            const fundingAmount = item.fundingAmount || 0;
            const fundingGap = fundingAmount + donated - item.estimate; // Corrected calculation

            return {
              ...item,
              vehicleID: item.vehicleID,
              category: "Vehicle",
              subcategory: item.sub_category,
              estimate: item.estimate,
              donated: donated,
              photoRecovery: item.photo_vehicle,
              funding: item.funding,
              fundingAmount: fundingAmount,
              fundingGap: fundingGap,
              fundingGapColor: fundingGap < 0 ? "red" : "green",
            };
          });
        setVehicleData(vehicleData.filter((item) => !item.is_deleted));

        const contentsData = contents
          .filter(filterBySelectedDisasters)
          .map((item) => {
            const donated = item.value || 0;
            const fundingAmount = item.fundingAmount || 0;
            const fundingGap = fundingAmount + donated - item.estimate; // Corrected calculation

            return {
              ...item,
              contentsID: item.contentsID,
              category: "Contents",
              subcategory: item.sub_category,
              estimate: item.estimate,
              donated: donated,
              photoRecovery: item.photo_contents,
              funding: item.funding,
              fundingAmount: fundingAmount,
              fundingGap: fundingGap,
              fundingGapColor: fundingGap < 0 ? "red" : "green",
            };
          });
        setContentsData(contentsData.filter((item) => !item.is_deleted));
      } else {
        console.error("No such document!");
      }
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  const fundingGapTemplate = (props) => {
    const color = props.fundingGapColor;
    const formattedValue = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Math.abs(props.fundingGap));
    return <span style={{ color: color }}>{formattedValue}</span>;
  };

  const [showPopup, setShowPopup] = useState(false);

  const toggleGiveAccess = () => {
    setShowPopup(!showPopup);
  };

  const onDialogClose = () => {
    setShowPopup(false);
  };

  const footerTemplate = () => {
    return (
      <div className="flex justify-end gap-3.5 p-2">
        <button
          className="text-gray-700 hover:bg-gray-100 cursor-pointer rounded border border-gray-300 px-3 py-1.5 font-semibold transition-all duration-300 ease-in-out hover:border-gray-500 hover:shadow-sm"
          onClick={toggleGiveAccess}
        >
          Cancel
        </button>
        <button className="flex cursor-pointer items-center gap-1 rounded bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white transition-all duration-300 ease-in-out hover:bg-blue-600 hover:shadow-md">
          Give Access
        </button>
      </div>
    );
  };

  const formatLastEditedTime = (time) => {
    if (!time) return "Never";

    const now = new Date();
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return time.toLocaleDateString();
  };

  // State for General section
  const [RecoveryPlanData, setRecoveryPlanData] = useState([]);
  const [isRecoveryPlanVisible, setIsRecoveryPlanVisible] = useState(true);
  const [lastRecoveryPlanEditedTime, setLasRecoveryPlanEditedTime] =
    useState(null);
  const [maxRecoveryPlanID, setMaxRecoveryPlanID] = useState(
    RecoveryPlanData.length > 0
      ? Math.max(...RecoveryPlanData.map((item) => item.RecoveryPlanID))
      : 0
  );

  const toggleRecoveryPlan = (event) => {
    event.preventDefault();
    setIsRecoveryPlanVisible(!isRecoveryPlanVisible);
  };

  const RecoveryPlanGridRef = useRef(null);

  const handleAddClickRecoveryPlan = () => {
    if (RecoveryPlanGridRef.current) {
      RecoveryPlanGridRef.current.addRecord();
    }
  };

  const actionBeginRecoveryPlan = (args) => {
    if (args.requestType === "save" && args.action === "edit") {
      if (args.data.funding) {
        console.log("Funding field updated:", args.data.funding);

        // Update the recoveryAssistanceData state
        setRecoveryAssistanceData((prevData) =>
          prevData.map((item) =>
            item.RecoveryPlanID === args.data.RecoveryPlanID
              ? { ...item, funding: args.data.funding }
              : item
          )
        );
      }
    }
  };

  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const generateUnmetNeedsReport = async () => {
    try {
      console.log("Starting report generation...");
      // Get the accessed user's data instead of current user
      const userDocRef = doc(db, "users", userId);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        console.error("Accessed user document not found");
        return;
      }

      const userData = userDoc.data();
      const citizenName = `${userData.first_name || ""} ${
        userData.last_name || ""
      }`.trim();

      let impactedAddressDoc = null;
      if (selectedDisasters.length === 1) {
        const disasterId = selectedDisasters[0];
        // Use accessedUserId for the impacted address path
        const impactedAddressRef = doc(
          db,
          `users/${userId}/impacted-addresses/${disasterId}`
        );
        impactedAddressDoc = await getDoc(impactedAddressRef);
      }

      const selectedDisasterNames = selectedDisasters
        .map((disasterId) => {
          const disaster = activeDisasters.find((d) => d.id === disasterId);
          return disaster ? disaster.name : "Unknown Disaster";
        })
        .join(", ");

      const pdf = new jsPDF("landscape", "pt", "a4");
      const margin = 40;
      const pageWidth = pdf.internal.pageSize.width;

      const loadLogoImage = () => {
        return new Promise((resolve) => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const img = new Image();
          canvas.width = 360;
          canvas.height = 360;

          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL("image/png", 1.0);
            resolve(dataUrl);
          };

          img.onerror = () => {
            console.error("Error loading logo");
            resolve(null);
          };

          img.src = blueSkyLogo;
        });
      };

      const logoImage = await loadLogoImage();

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.text("UNMET NEEDS REPORT", margin, margin + 10);

      if (logoImage) {
        const logoWidth = 60;
        const logoHeight = 60;
        const logoX = pageWidth - margin - logoWidth;
        try {
          pdf.addImage(
            logoImage,
            "PNG",
            logoX,
            margin - 20,
            logoWidth,
            logoHeight,
            "",
            "FAST",
            "NONE"
          );
        } catch (logoError) {
          console.error("Error adding logo to PDF:", logoError);
        }
      }

      pdf.setFontSize(16);
      pdf.text(selectedDisasterNames, pageWidth / 2, margin + 70, {
        align: "center",
      });

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      const currentDate = new Date().toLocaleDateString();
      pdf.text(currentDate, pageWidth - margin, margin + 70, {
        align: "right",
      });

      let disasterInfoY = margin + 90;

      // Dynamically show "Disaster No:" only if disasterNumber exists
      if (disasterNumber) {
        const disasterText = "Disaster No:";
        const disasterTextWidth = pdf.getTextWidth(disasterText);
        const disasterNumberWidth = pdf.getTextWidth(`${disasterNumber}`);

        // Total width of the text block (label + number + spacing)
        const totalWidth = disasterTextWidth + disasterNumberWidth + 10;

        // Center position accounting for both elements
        const centerX = pageWidth / 2 - totalWidth / 2;

        pdf.text(disasterText, centerX, disasterInfoY);
        pdf.text(
          `${disasterNumber}`,
          centerX + disasterTextWidth + 10,
          disasterInfoY
        );
      } else {
        // Move other elements up if "Disaster No:" is not displayed
        disasterInfoY -= 20;
      }

      let leftY = margin + 100;
      pdf.setFont("helvetica", "bold");
      pdf.text(`${citizenName}`, margin, leftY);
      leftY += 20;

      pdf.setFont("helvetica", "normal");

      // Helper function to add text dynamically
      const addText = (text) => {
        if (text) {
          pdf.text(text, margin, leftY);
          leftY += 20;
        }
      };

      addText(userData.address1);
      addText(userData.address2);
      addText(
        [userData.city, userData.state, userData.zip].filter(Boolean).join(", ")
      );
      addText(userData.mobile_number);
      addText(userData.email);

      let rightY = margin + 100;
      const rightX = pageWidth - margin;

      pdf.setFont("helvetica", "bold");
      pdf.text("IMPACTED ADDRESS", rightX, rightY, { align: "right" });
      rightY += 20;

      pdf.setFont("helvetica", "normal");

      // Use impacted address data if available, otherwise fallback to user data
      let impactedData = impactedAddressDoc?.exists()
        ? impactedAddressDoc.data()
        : userData;

      const addTextRight = (text) => {
        if (text) {
          pdf.text(text, rightX, rightY, { align: "right" });
          rightY += 20;
        }
      };

      addTextRight(impactedData.address1);
      addTextRight(impactedData.address2);
      addTextRight(
        [impactedData.city, impactedData.state, impactedData.zip]
          .filter(Boolean)
          .join(", ")
      );

      // Dynamically show "Case No:" and "FEMA No:" only if they exist
      let caseInfoY = rightY;

      pdf.setFont("helvetica", "normal");

      if (caseNumber) {
        pdf.text("Case No:", rightX - 100, caseInfoY, { align: "right" });
        pdf.text(`${caseNumber}`, rightX, caseInfoY, { align: "right" });
        caseInfoY += 15;
      }

      if (femaNumber) {
        pdf.text("FEMA No:", rightX - 100, caseInfoY, { align: "right" });
        pdf.text(`${femaNumber}`, rightX, caseInfoY, { align: "right" });
      }

      let yOffset = Math.max(leftY, rightY) + 40;

      const tableWidth = pageWidth - 2 * margin; // Ensure uniform table width

      const columnStyles = {
        0: { cellWidth: tableWidth * 0.4 }, // Category Column
        1: { cellWidth: tableWidth * 0.2, halign: "right" }, // Estimate
        2: { cellWidth: tableWidth * 0.2, halign: "right" }, // Donated
        3: { cellWidth: tableWidth * 0.2, halign: "right" }, // Gap
      };

      const formatCurrency = (value) => {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(value || 0);
      };

      const categories = [
        {
          title: "General",
          data: generalData.filter((item) => {
            const estimate = parseFloat(item.estimate) || 0;
            const donated = parseFloat(item.value) || 0;
            return estimate > donated;
          }),
        },
        {
          title: "Home Repair",
          data: homeRepairData.filter((item) => {
            const estimate = parseFloat(item.estimate) || 0;
            const donated = parseFloat(item.value) || 0;
            return estimate > donated;
          }),
        },
        {
          title: "Landscaping",
          data: landscapingData.filter((item) => {
            const estimate = parseFloat(item.estimate) || 0;
            const donated = parseFloat(item.value) || 0;
            return estimate > donated;
          }),
        },
        {
          title: "Vehicle",
          data: vehicleData.filter((item) => {
            const estimate = parseFloat(item.estimate) || 0;
            const donated = parseFloat(item.value) || 0;
            return estimate > donated;
          }),
        },
        {
          title: "Contents",
          data: contentsData.filter((item) => {
            const estimate = parseFloat(item.estimate) || 0;
            const donated = parseFloat(item.value) || 0;
            return estimate > donated;
          }),
        },
      ];

      for (const category of categories) {
        if (!category.data || category.data.length === 0) continue;

        pdf.setFont(undefined, "bold");
        pdf.setFontSize(14);
        pdf.text(category.title, margin, yOffset);

        const tableData = category.data.map((item) => [
          { content: item.sub_category, styles: { halign: "left" } },
          {
            content: formatCurrency(item.estimate),
            styles: { halign: "right" },
          },
          { content: formatCurrency(item.value), styles: { halign: "right" } },
          {
            content: formatCurrency(
              parseFloat(item.estimate) - parseFloat(item.value || 0)
            ),
            styles: { halign: "right" },
          },
        ]);

        const totals = category.data.reduce(
          (acc, item) => {
            const estimate = parseFloat(item.estimate) || 0;
            const donated = parseFloat(item.value) || 0;
            const gap = estimate - donated;

            return {
              estimate: acc.estimate + estimate,
              donated: acc.donated + donated,
              gap: acc.gap + gap,
            };
          },
          { estimate: 0, donated: 0, gap: 0 }
        );

        tableData.push([
          { content: "TOTAL", styles: { halign: "left", fontStyle: "bold" } },
          {
            content: formatCurrency(totals.estimate),
            styles: { halign: "right", fontStyle: "bold" },
          },
          {
            content: formatCurrency(totals.donated),
            styles: { halign: "right", fontStyle: "bold" },
          },
          {
            content: formatCurrency(totals.gap),
            styles: { halign: "right", fontStyle: "bold" },
          },
        ]);

        pdf.autoTable({
          startY: yOffset + 10,
          head: [
            [
              { content: "Category", styles: { halign: "left" } },
              { content: "Estimate", styles: { halign: "right" } },
              { content: "Donated", styles: { halign: "right" } },
              { content: "Gap", styles: { halign: "right" } },
            ],
          ],
          body: tableData,
          styles: { fontSize: 10, cellPadding: 5 },
          headStyles: {
            fillColor: [13, 71, 161],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: "bold",
          },
          footStyles: { fillColor: [240, 240, 240], fontStyle: "bold" },
          theme: "grid",
          tableWidth: tableWidth, // Ensure all tables have the same width
          columnStyles: columnStyles, // Apply uniform column widths
        });

        yOffset = pdf.previousAutoTable.finalY + 40;

        if (
          yOffset > pdf.internal.pageSize.height - 100 &&
          category !== categories[categories.length - 1]
        ) {
          pdf.addPage();
          yOffset = 40;
        }
      }

      const grandTotals = categories.reduce(
        (acc, category) => {
          if (!category.data) return acc;

          const categoryTotals = category.data.reduce(
            (catAcc, item) => {
              const estimate = parseFloat(item.estimate) || 0;
              const donated = parseFloat(item.value) || 0;
              const gap = estimate - donated;

              return {
                estimate: catAcc.estimate + estimate,
                donated: catAcc.donated + donated,
                gap: catAcc.gap + gap,
              };
            },
            { estimate: 0, donated: 0, gap: 0 }
          );

          return {
            estimate: acc.estimate + categoryTotals.estimate,
            donated: acc.donated + categoryTotals.donated,
            gap: acc.gap + categoryTotals.gap,
          };
        },
        { estimate: 0, donated: 0, gap: 0 }
      );

      if (yOffset > pdf.internal.pageSize.height - 150) {
        pdf.addPage();
        yOffset = 40;
      }

      pdf.setFont(undefined, "bold");
      pdf.setFontSize(16);
      pdf.text("GRAND TOTAL", margin, yOffset);

      pdf.autoTable({
        startY: yOffset + 10,
        head: [
          [
            { content: "CATEGORY", styles: { halign: "left" } },
            { content: "ESTIMATE", styles: { halign: "right" } },
            { content: "DONATED", styles: { halign: "right" } },
            { content: "GAP", styles: { halign: "right" } },
          ],
        ],
        body: [
          [
            {
              content: "ALL CATEGORIES",
              styles: { halign: "left", fontStyle: "bold" },
            },
            {
              content: formatCurrency(grandTotals.estimate),
              styles: { halign: "right", fontStyle: "bold" },
            },
            {
              content: formatCurrency(grandTotals.donated),
              styles: { halign: "right", fontStyle: "bold" },
            },
            {
              content: formatCurrency(grandTotals.gap),
              styles: { halign: "right", fontStyle: "bold" },
            },
          ],
        ],
        styles: { fontSize: 12, cellPadding: 8 },
        headStyles: {
          fillColor: [13, 71, 161],
          textColor: [255, 255, 255],
          fontSize: 12,
          fontStyle: "bold",
        },
        theme: "grid",
        tableWidth: tableWidth, // Ensure same width as other tables
        columnStyles: columnStyles, // Ensure column consistency
      });

      // Instead of pdf.save(), create a data URL
      const pdfDataUrl = pdf.output("dataurlstring");
      setPdfUrl(pdfDataUrl);
      setIsPdfDialogOpen(true);
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Error generating report. Please try again.");
    }
  };

  // Helper function to calculate section gap
  const calculateSectionGap = (data) => {
    return data.reduce((total, item) => {
      const estimate = parseFloat(item.estimate) || 0;
      const value = parseFloat(item.value) || 0;

      return total + (estimate - value);
    }, 0);
  };

  /*
  const imageEditRecoveryPlan = {
      create: () => {
          elem = document.createElement('div');
          return elem;
      },
      read: () => {
          return strm;
      },
      destroy: () => {
          if (uploadObj) {
              uploadObj.destroy();
          }
          strm = null;
      },
      write: args => {
          const path = {
              removeUrl: 'https://services.syncfusion.com/react/production/api/FileUploader/Remove',
              saveUrl: 'https://services.syncfusion.com/react/production/api/FileUploader/Save'
          };

          elem.innerHTML = `
              <div class="flex flex-col justify-center w-full">
                  <label htmlFor="access" class="block mb-2 text-xs text-gray-500">Photo Attachment</label>
                  <div class="flex flex-col items-center">
                      <div id="imagePreview" class="mb-4 flex flex-col items-center justify-center">
                          <!-- Image will be dynamically inserted here -->
                      </div>
                      <div id="dropArea">
                          <label class="upload-button" id="uploadLabel">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="17 8 12 3 7 8"></polyline>
                                  <line x1="12" y1="3" x2="12" y2="15"></line>
                              </svg>
                              Upload Image
                          </label>
                          <input type="file" id="defaultUpload" style="display:none;" />
                      </div>
                  </div>
              </div>
          `;

          const inputElement = elem.querySelector('#defaultUpload');
          const uploadLabel = elem.querySelector('#uploadLabel');
          const imagePreview = elem.querySelector('#imagePreview');

          uploadObj = new Uploader({
              asyncSettings: path,
              success: onUploadSuccess,
              failure: onUploadFailure,
              multiple: false,
              allowedExtensions: ".jpeg, .png, .jpg",
              cssClass: "hidden"
          });

          uploadObj.appendTo(inputElement);

          uploadLabel.addEventListener('click', () => {
              inputElement.click();
          });

          inputElement.addEventListener('change', () => {
              if (inputElement.files.length > 0) {
                  uploadObj.upload(inputElement.files);
              }
          });

          function onUploadSuccess(args) {
              if (args.operation === 'upload') {
                  const file = args.file.rawFile;
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  reader.onload = function () {
                      strm = reader.result;
                      displayUploadedImage(strm, args.file.name);
                  };
                  reader.onerror = function (error) {
                      console.log('Error: ', error);
                  };
              }
          }

          function onUploadFailure(args) {
              console.log('File failed to upload');
          }

          function displayUploadedImage(imageSrc, fileName) {
              imagePreview.innerHTML = `
                  <img src="${imageSrc}" alt="Uploaded Image" style="width: 100px; height: 100px; border-radius: 50%; object-fit: cover;" />
                  <p class="text-sm text-blue-500 mt-2">${fileName}</p>
              `;
          }

          // Display the current image if available
          if (args.rowData.photoRecoveryPlan) {
              displayUploadedImage(args.rowData.photoRecoveryPlan, 'Current Image');
          } else {
              imagePreview.innerHTML = '';
          }
      }
  };
  */

  /*

  const actionBeginRecoveryPlan = (args) => {
      if (args.requestType === 'save' && args.action === 'add') {
        const newRecoveryPlanID = maxRecoveryPlanID + 1;
        args.data.RecoveryPlanID = newRecoveryPlanID;
        setMaxRecoveryPlanID(newRecoveryPlanID);
      }
      if (args.requestType === 'beginEdit') {
          selectedRow = args.rowData;
      }
  };

  const actionCompleteRecoveryPlan = (args) => {

      if (args.requestType === 'save' && args.action === 'edit') {
          const updatedData = RecoveryPlanData.map(item =>
              item.RecoveryPlanID === selectedRow.RecoveryPlanID ? args.data : item
          );
          console.log('Data saved successfully:', args.data);
          setRecoveryPlanData(updatedData);
      }
  };

  */

  const dialogRef = useRef(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (isDialogOpen && dialogRef.current) {
      dialogRef.current.show();
    } else if (!isDialogOpen && dialogRef.current) {
      dialogRef.current.hide();
    }
  }, [isDialogOpen]);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  // Update the grid templates
  const gridTemplate = (props) => {
    const images = Array.isArray(props.photo_general)
      ? props.photo_general
      : props.photo_general
      ? [props.photo_general]
      : [];
    return renderImagePreview(images);
  };

  const landscapingTemplate = (props) => {
    const images = Array.isArray(props.photo_landscaping)
      ? props.photo_landscaping
      : props.photo_landscaping
      ? [props.photo_landscaping]
      : [];
    return renderImagePreview(images);
  };

  const contentsTemplate = (props) => {
    const images = Array.isArray(props.photo_contents)
      ? props.photo_contents
      : props.photo_contents
      ? [props.photo_contents]
      : [];
    return renderImagePreview(images);
  };

  const vehicleTemplate = (props) => {
    const images = Array.isArray(props.photo_vehicle)
      ? props.photo_vehicle
      : props.photo_vehicle
      ? [props.photo_vehicle]
      : [];
    return renderImagePreview(images);
  };

  const homeRepairTemplate = (props) => {
    const images = Array.isArray(props.photo_home_repair)
      ? props.photo_home_repair
      : props.photo_home_repair
      ? [props.photo_home_repair]
      : [];
    return renderImagePreview(images);
  };

  // Helper function to render image preview
  const renderImagePreview = (images) => {
    return (
      <div
        className="image-name-container"
        onClick={() => handleImageClick(images)}
        style={{
          cursor: "pointer",
          padding: "10px",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          textAlign: "center",
          minWidth: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#3B82F6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginRight: "8px" }}
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <p className="text-sm">
          {images.length > 0
            ? `${images.length} image${images.length > 1 ? "s" : ""}`
            : "No images"}
        </p>
      </div>
    );
  };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const handleImageClick = (images) => {
    setSelectedImages(Array.isArray(images) ? images : [images]);
    setCurrentImageIndex(0);
    setIsImageDialogOpen(true);
  };

  const sanitizeData = (data) => {
    const sanitizedData = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        sanitizedData[key] = data[key] || "";
      }
    }
    return sanitizedData;
  };

  const { categories, subcategories } = disasterCategories;

  //General
  const [generalData, setGeneralData] = useState([]);
  const [isGeneralVisible, setIsGeneralVisible] = useState(true);
  const [lastGeneralEditedTime, setLastGeneralEditedTime] = useState(null);

  const toggleGeneral = (event) => {
    event.preventDefault();
    setIsGeneralVisible(!isGeneralVisible);
  };

  const generalGridRef = useRef(null);

  const actionBeginGeneral = async (args) => {
    if (args.requestType === "beginEdit") {
      selectedRow = args.rowData;
    }

    if (args.requestType === "delete") {
      try {
        for (const docData of args.data) {
          const documentId = docData.generalID;
          const fileUrl = docData.photo_general;

          if (fileUrl) {
            const storage = getStorage();
            const fileRef = ref(storage, fileUrl);

            await deleteObject(fileRef);
          }

          await DisasterMgtService.updateRecord(
            userId,
            documentId,
            { is_deleted: true, last_edited: new Date() }, // Add last_edited here
            "general"
          );
        }

        // Update the last edited time after deletion
        setLastGeneralEditedTime(new Date());
      } catch (error) {
        console.error("Error updating document status:", error);
      }
    }
  };

  const actionCompleteGeneral = async (args) => {
    if (args.requestType === "save") {
      if (args.action === "edit") {
        const documentId = selectedRow.generalID;

        const sanitizedData = sanitizeData(args.data);
        sanitizedData.is_deleted = false;

        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "general"
        );
      }

      const documentId = args.data.generalID;
      let updatedData = { ...args.data };
      const sanitizedData = sanitizeData(updatedData);
      // Refresh the grid data
      try {
        // Update the home repair record with the new data
        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "general"
        );

        // Refresh the grid data
        const updatedGeneralData = await DisasterMgtService.getGeneral(userId);
        setGeneralData(updatedGeneralData);
      } catch (error) {
        console.error("Error fetching updated data:", error);
        // Optionally, you can add some user feedback here about the error
      }
      await fetchData();
    }
  };

  //Home Repair
  const [isHomeRepairVisible, setIsHomeRepairVisible] = useState(true);
  const [lastHomeRepairEditedTime, setLastHomeRepairEditedTime] =
    useState(null);
  const [homeRepairData, setHomeRepairData] = useState([]);

  const toggleHomeRepair = (event) => {
    event.preventDefault();
    setIsHomeRepairVisible(!isHomeRepairVisible);
  };

  const homeRepairGridRef = useRef(null);

  const actionBeginHomeRepair = async (args) => {
    if (args.requestType === "beginEdit") {
      selectedRow = args.rowData;
    }

    if (args.requestType === "delete") {
      try {
        for (const docData of args.data) {
          const documentId = docData.homeRepairID;
          const fileUrl = docData.photo_home_repair;

          if (fileUrl) {
            const storage = getStorage();
            const fileRef = ref(storage, fileUrl);

            await deleteObject(fileRef);
          }

          await DisasterMgtService.updateRecord(
            userId,
            documentId,
            { is_deleted: true, last_edited: new Date() }, // Add last_edited here
            "home_repair"
          );
        }

        // Update the last edited time after deletion
        setLastHomeRepairEditedTime(new Date());
      } catch (error) {
        console.error("Error updating document status:", error);
      }
    }
  };

  const actionCompleteHomeRepair = async (args) => {
    if (args.requestType === "save") {
      if (args.action === "edit") {
        const documentId = selectedRow.homeRepairID;

        const sanitizedData = sanitizeData(args.data);
        sanitizedData.is_deleted = false;

        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "home_repair"
        );
      }

      const documentId = args.data.homeRepairID;
      let updatedData = { ...args.data };
      const sanitizedData = sanitizeData(updatedData);
      // Refresh the grid data
      try {
        // Update the home repair record with the new data
        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "home_repair"
        );

        // Refresh the grid data
        const updatedHomeRepairData = await DisasterMgtService.getHomeRepair(
          userId
        );
        setHomeRepairData(updatedHomeRepairData);
      } catch (error) {
        console.error("Error fetching updated data:", error);
        // Optionally, you can add some user feedback here about the error
      }
    }
  };

  //Landscaping
  const [isLandscapingVisible, setIsLandscapingVisible] = useState(true);
  const [lastLandscapingEditedTime, setLastLandscapingEditedTime] =
    useState(null);
  const [landscapingData, setLandscapingData] = useState([]);

  const toggleLandscaping = (event) => {
    event.preventDefault();
    setIsLandscapingVisible(!isLandscapingVisible);
  };

  const landscapingGridRef = useRef(null);

  const actionBeginLandscaping = async (args) => {
    if (args.requestType === "beginEdit") {
      selectedRow = args.rowData;
    }

    if (args.requestType === "delete") {
      try {
        for (const docData of args.data) {
          const documentId = docData.landscapingID;
          const fileUrl = docData.photo_landscaping;

          if (fileUrl) {
            const storage = getStorage();
            const fileRef = ref(storage, fileUrl);

            await deleteObject(fileRef);
          }

          await DisasterMgtService.updateRecord(
            userId,
            documentId,
            { is_deleted: true, last_edited: new Date() }, // Add last_edited here
            "landscaping"
          );
        }

        // Update the last edited time after deletion
        setLastLandscapingEditedTime(new Date());
      } catch (error) {
        console.error("Error updating document status:", error);
      }
    }
  };

  const actionCompleteLandscaping = async (args) => {
    if (args.requestType === "save") {
      if (args.action === "edit") {
        const documentId = selectedRow.landscapingID;

        const sanitizedData = sanitizeData(args.data);
        sanitizedData.is_deleted = false;

        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "landscaping"
        );
      }

      const documentId = args.data.landscapingID;
      let updatedData = { ...args.data };
      const sanitizedData = sanitizeData(updatedData);
      // Refresh the grid data
      try {
        // Update the home repair record with the new data
        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "landscaping"
        );

        // Refresh the grid data
        const updatedLandscapingData = await DisasterMgtService.getLandscaping(
          userId
        );
        setLandscapingData(updatedLandscapingData);
      } catch (error) {
        console.error("Error fetching updated data:", error);
        // Optionally, you can add some user feedback here about the error
      }
      await fetchData();
    }
  };

  //Landscaping
  const [vehicleData, setVehicleData] = useState([]);
  const [isVehicleVisible, setIsVehicleVisible] = useState(true);
  const [lastVehicleEditedTime, setLastVehicleEditedTime] = useState(null);

  const toggleVehicle = (event) => {
    event.preventDefault();
    setIsVehicleVisible(!isVehicleVisible);
  };

  const vehicleGridRef = useRef(null);

  const actionBeginVehicle = async (args) => {
    if (args.requestType === "beginEdit") {
      selectedRow = args.rowData;
    }

    if (args.requestType === "delete") {
      try {
        for (const docData of args.data) {
          const documentId = docData.vehicleID;
          const fileUrl = docData.photo_vehicle;

          if (fileUrl) {
            const storage = getStorage();
            const fileRef = ref(storage, fileUrl);

            await deleteObject(fileRef);
          }

          await DisasterMgtService.updateRecord(
            userId,
            documentId,
            { is_deleted: true, last_edited: new Date() }, // Add last_edited here
            "vehicle"
          );
        }

        // Update the last edited time after deletion
        setLastVehicleEditedTime(new Date());
      } catch (error) {
        console.error("Error updating document status:", error);
      }
    }
  };

  const actionCompleteVehicle = async (args) => {
    if (args.requestType === "save") {
      if (args.action === "edit") {
        const documentId = selectedRow.vehicleID;

        const sanitizedData = sanitizeData(args.data);
        sanitizedData.is_deleted = false;

        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "vehicle"
        );
      }

      const documentId = args.data.vehicleID;
      let updatedData = { ...args.data };
      const sanitizedData = sanitizeData(updatedData);
      // Refresh the grid data
      try {
        // Update the home repair record with the new data
        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "vehicle"
        );

        // Refresh the grid data
        const updatedVehicleData = await DisasterMgtService.getVehicle(userId);
        setVehicleData(updatedVehicleData);
      } catch (error) {
        console.error("Error fetching updated data:", error);
        // Optionally, you can add some user feedback here about the error
      }
      await fetchData();
    }
  };

  //Contents
  const [contentsData, setContentsData] = useState([]);
  const [isContentsVisible, setIsContentsVisible] = useState(true);
  const [lastContentsEditedTime, setLastContentsEditedTime] = useState(null);

  const toggleContents = (event) => {
    event.preventDefault();
    setIsContentsVisible(!isContentsVisible);
  };

  const contentsGridRef = useRef(null);

  const actionBeginContents = async (args) => {
    if (args.requestType === "beginEdit") {
      selectedRow = args.rowData;
    }

    if (args.requestType === "delete") {
      try {
        for (const docData of args.data) {
          const documentId = docData.contentsID;
          const fileUrl = docData.photo_contents;

          if (fileUrl) {
            const storage = getStorage();
            const fileRef = ref(storage, fileUrl);

            await deleteObject(fileRef);
          }

          await DisasterMgtService.updateRecord(
            userId,
            documentId,
            { is_deleted: true, last_edited: new Date() }, // Add last_edited here
            "contents"
          );
        }

        // Update the last edited time after deletion
        setLastContentsEditedTime(new Date());
      } catch (error) {
        console.error("Error updating document status:", error);
      }
    }
  };

  const actionCompleteContents = async (args) => {
    if (args.requestType === "save") {
      if (args.action === "edit") {
        const documentId = selectedRow.contentsID;

        const sanitizedData = sanitizeData(args.data);
        sanitizedData.is_deleted = false;

        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "contents"
        );
      }

      const documentId = args.data.contentsID;
      let updatedData = { ...args.data };
      const sanitizedData = sanitizeData(updatedData);
      // Refresh the grid data
      try {
        // Update the home repair record with the new data
        await DisasterMgtService.updateRecord(
          userId,
          documentId,
          sanitizedData,
          "contents"
        );

        // Refresh the grid data
        const updatedContentsData = await DisasterMgtService.getContents(
          userId
        );
        setContentsData(updatedContentsData);
      } catch (error) {
        console.error("Error fetching updated data:", error);
        // Optionally, you can add some user feedback here about the error
      }
      await fetchData();
    }
  };

  const toolbarOptions = ["Edit", "Update", "Cancel"];
  const [disasterMap, setDisasterMap] = useState({});
  const [dialogContent, setDialogContent] = useState("");
  const [isDialogDisasterOpen, setIsDialogDisasterOpen] = useState(false);

  useEffect(() => {
    const fetchDisasters = async () => {
      try {
        const disastersQuery = query(collection(db, "declared-disaster"));
        const querySnapshot = await getDocs(disastersQuery);
        const fetchedDisasters = querySnapshot.docs.reduce((acc, doc) => {
          if (doc.data().disaster_status === "active") {
            acc[doc.id] = doc.data().disaster_name;
          }
          return acc;
        }, {});

        setDisasterMap(fetchedDisasters);
      } catch (error) {
        console.error("Error fetching disasters:", error);
      }
    };

    fetchDisasters();
  }, []);

  const categoryTemplate = (props) => {
    const disasterCount = props.associatedDisasters
      ? props.associatedDisasters.length
      : 0;

    let editorTypeColor = "";
    let editorTypeLetter = "";
    switch (props.editor_type) {
      case "center":
        editorTypeColor = "bg-green-500";
        editorTypeLetter = "C";
        break;
      case "partner":
        editorTypeColor = "bg-yellow-500";
        editorTypeLetter = "P";
        break;
      case "citizen":
        editorTypeColor = "bg-blue-500";
        editorTypeLetter = "S";
        break;
      default:
        editorTypeColor = "bg-gray-500";
        editorTypeLetter = "?";
    }

    return (
      <div className="flex items-center">
        <span className="mr-2">{props.sub_category}</span>
        <div className="flex items-center space-x-1">
          {/* <span
            className={`h-6 w-6 rounded-full ${editorTypeColor} flex items-center justify-center text-xs font-bold text-white`}
            title={`Editor Type: ${props.editor_type}`}
          >
            {editorTypeLetter}
          </span> */}
          {/* {disasterCount > 0 && (
            <span 
              className="bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded cursor-pointer hover:bg-red-600"
              onClick={() => handleDisasterClick(props.associatedDisasters)}
              title={`${disasterCount} Associated Disaster${
                disasterCount > 1 ? "s" : ""
              }`}
            >
              {disasterCount}
            </span>
          )} */}
        </div>
      </div>
    );
  };

  const handleDisasterClick = (disasterIds) => {
    if (disasterIds && disasterIds.length > 0) {
      const disasterList = disasterIds
        .map(
          (id, index) =>
            `${index + 1}. ${disasterMap[id] || "Unknown Disaster"}`
        )
        .join("\n");
      setDialogContent(disasterList);
      setIsDialogDisasterOpen(true);
    } else {
      setDialogContent("No associated disasters.");
      setIsDialogDisasterOpen(true);
    }
  };

  const closeDialogDisaster = () => {
    setIsDialogDisasterOpen(false);
  };

  const calculateTotals = (data) => {
    return {
      totalEstimate: data.reduce((acc, item) => acc + (item.estimate || 0), 0),
      totalDonated: data.reduce((acc, item) => acc + (item.value || 0), 0),
      totalFundingGap: data.reduce(
        (acc, item) => acc + (item.fundingGap || 0),
        0
      ),
    };
  };

  const generalTotals = calculateTotals(generalData);
  const homeRepairTotals = calculateTotals(homeRepairData);
  const landscapingTotals = calculateTotals(landscapingData);
  const vehicleTotals = calculateTotals(vehicleData);
  const contentsTotals = calculateTotals(contentsData);

  // Add FileDialog component
  const FileDialog = ({ isOpen, onClose, fileUrl }) => {
    if (!isOpen || !fileUrl) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="relative z-50 flex h-[781px] w-[1200px] flex-col overflow-hidden rounded-[10px] bg-white shadow-preset">
          <div className="flex items-center justify-between border-b bg-tropical-blue px-4 py-3">
            <h2 className="text-xl font-semibold text-dark-cerulean">
              Unmet Needs Report
            </h2>
            <button onClick={onClose} className="cursor-pointer bg-transparent">
              <svg
                width="40"
                height="40"
                viewBox="0 0 32 33"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <mask
                  id="mask0_10134_3366"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="32"
                  height="33"
                >
                  <rect y="0.5" width="32" height="32" fill="#D9D9D9" />
                </mask>
                <g mask="url(#mask0_10134_3366)">
                  <circle
                    cx="16.0013"
                    cy="16.4974"
                    r="13.3333"
                    fill="#FAFAFA"
                  />
                  <path
                    d="M16.0013 18.3641L19.868 22.2307C20.1124 22.4752 20.4235 22.5974 20.8013 22.5974C21.1791 22.5974 21.4902 22.4752 21.7346 22.2307C21.9791 21.9863 22.1013 21.6752 22.1013 21.2974C22.1013 20.9196 21.9791 20.6085 21.7346 20.3641L17.868 16.4974L21.7346 12.6307C21.9791 12.3863 22.1013 12.0752 22.1013 11.6974C22.1013 11.3196 21.9791 11.0085 21.7346 10.7641C21.4902 10.5196 21.1791 10.3974 20.8013 10.3974C20.4235 10.3974 20.1124 10.5196 19.868 10.7641L16.0013 14.6307L12.1346 10.7641C11.8902 10.5196 11.5791 10.3974 11.2013 10.3974C10.8235 10.3974 10.5124 10.5196 10.268 10.7641C10.0235 11.0085 9.9013 11.3196 9.9013 11.6974C9.9013 12.0752 10.0235 12.3863 10.268 12.6307L14.1346 16.4974L10.268 20.3641C10.0235 20.6085 9.9013 20.9196 9.9013 21.2974C9.9013 21.6752 10.0235 21.9863 10.268 22.2307C10.5124 22.4752 10.8235 22.5974 11.2013 22.5974C11.5791 22.5974 11.8902 22.4752 12.1346 22.2307L16.0013 18.3641ZM16.0013 29.8307C14.1569 29.8307 12.4235 29.4807 10.8013 28.7807C9.17908 28.0807 7.76797 27.1307 6.56797 25.9307C5.36797 24.7307 4.41797 23.3196 3.71797 21.6974C3.01797 20.0752 2.66797 18.3418 2.66797 16.4974C2.66797 14.653 3.01797 12.9196 3.71797 11.2974C4.41797 9.67517 5.36797 8.26406 6.56797 7.06406C7.76797 5.86406 9.17908 4.91406 10.8013 4.21406C12.4235 3.51406 14.1569 3.16406 16.0013 3.16406C17.8457 3.16406 19.5791 3.51406 21.2013 4.21406C22.8235 4.91406 24.2346 5.86406 25.4346 7.06406C26.6346 8.26406 27.5846 9.67517 28.2846 11.2974C28.9846 12.9196 29.3346 14.653 29.3346 16.4974C29.3346 18.3418 28.9846 20.0752 28.2846 21.6974C27.5846 23.3196 26.6346 24.7307 25.4346 25.9307C24.2346 27.1307 22.8235 28.0807 21.2013 28.7807C19.5791 29.4807 17.8457 29.8307 16.0013 29.8307Z"
                    fill="#E72528"
                  />
                </g>
              </svg>
            </button>
          </div>
          <div className="relative flex-grow">
            <iframe
              src={fileUrl}
              title="Unmet Needs Report"
              className="absolute inset-0 h-full w-full"
              style={{ background: "white" }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex h-full w-full flex-col gap-4">
        {isDialogDisasterOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
              <h2 className="bg-blue-500 p-4 text-xl font-bold text-white">
                Associated Disasters
              </h2>
              <div className="px-6 py-4">
                <pre className="whitespace-pre-wrap text-base">
                  {dialogContent}
                </pre>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={closeDialogDisaster}
                    className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="relative mb-2 inline-block max-w-full font-poppins font-medium leading-[21px] text-darkslategray-200">
          <p>
            The Recovery Plan is a vital document that outlines the steps and
            strategies for rebuilding and restoring your community after a
            disaster. By completing this plan, you contribute to a coordinated,
            efficient recovery process. Please provide detailed information to
            ensure the most effective path forward for long-term recovery and
            resilience.
          </p>
        </div>
        <div className="w-full">
          <section className="box-border h-auto self-stretch overflow-hidden bg-white ">
            <header className="mb-2 flex flex-row items-center justify-between self-stretch">
              <div></div>
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-2 rounded bg-[#003460] px-4 py-3 text-sm text-[#F7CA41] shadow transition duration-200"
                  onClick={generateUnmetNeedsReport}
                >
                  <svg
                    width="22"
                    height="19"
                    viewBox="0 0 22 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.0711 0.359375C11.5465 0.359375 11.1242 0.781749 11.1242 1.30634V8.012C11.1242 8.53659 11.5465 8.95889 12.0711 8.95889H21.0531C21.5777 8.95889 22 8.53659 22 8.012V1.30634C22 0.781749 21.5777 0.359375 21.0531 0.359375H12.0711ZM15.193 2.98929H17.9505C18.3033 2.98929 18.5872 3.27327 18.5872 3.626C18.5872 3.97874 18.3033 4.26263 17.9505 4.26263H15.193C14.8403 4.26263 14.5563 3.97874 14.5563 3.626C14.5563 3.27327 14.8403 2.98929 15.193 2.98929ZM6.15276 8.48843C6.10793 8.48696 6.06508 8.48844 6.02432 8.49022C5.86134 8.49949 5.73217 8.53633 5.63109 8.58009C5.53001 8.62386 5.45704 8.67448 5.42051 8.6998C5.38399 8.72513 5.3841 8.7252 5.38191 10.0929C5.3797 11.4606 5.37527 14.196 5.3818 15.5746C5.38827 16.9531 5.40576 16.9748 5.47318 17.0009C5.54058 17.027 5.65797 17.0575 6.71909 17.3336C7.78021 17.6098 9.78495 18.1316 10.8439 18.3948C11.9028 18.6579 12.0159 18.6622 12.1398 18.6383C12.2638 18.6144 12.3986 18.5622 12.6835 18.4513C12.9683 18.3404 13.4032 18.1708 14.3904 17.7947C15.3775 17.4185 16.917 16.8357 17.8194 16.4856C18.7218 16.1356 18.987 16.0182 19.1131 15.9116C19.2392 15.8051 19.2262 15.7094 19.2001 15.605C19.174 15.5007 19.1349 15.3875 19.0327 15.2114C18.9305 15.0353 18.7652 14.7961 18.5761 14.62C18.3869 14.4439 18.1738 14.3308 17.9433 14.2917C17.7128 14.2525 17.4649 14.2873 16.3451 14.3026C15.2253 14.3178 13.2336 14.3135 12.1595 14.3091C11.0853 14.3046 10.9287 14.3004 10.7787 14.2786C10.6286 14.2568 10.4852 14.2178 10.2264 14.109C9.96766 14.0003 9.59365 13.822 9.40448 13.6871C9.21529 13.5523 9.21094 13.461 9.2131 13.3349C9.21531 13.2088 9.224 13.0478 9.28265 12.926C9.34136 12.8043 9.45012 12.7217 9.54361 12.6782C9.63712 12.6347 9.71539 12.6303 9.81427 12.6742C9.91315 12.7179 10.0326 12.8097 10.3551 12.9153C10.6776 13.0209 11.2031 13.1403 11.8102 13.1938C12.4174 13.2474 13.1062 13.2351 13.5274 13.189C13.9487 13.1428 14.1025 13.0629 14.2132 12.9891C14.3239 12.9153 14.3916 12.8476 14.4377 12.7061C14.4838 12.5647 14.5084 12.3496 14.5134 12.1412C14.5184 11.9328 14.504 11.7313 14.4702 11.5744C14.4364 11.4176 14.3832 11.3055 14.2736 11.2217C14.164 11.138 13.9979 11.0825 13.7089 10.9872C13.4198 10.8919 13.0078 10.7566 11.9223 10.3723C10.8368 9.98788 9.07785 9.35441 8.07537 9.00385C7.0729 8.6533 6.8269 8.58573 6.60549 8.54268C6.43945 8.5104 6.28724 8.49174 6.15276 8.48843ZM1.75483 8.6947C0.782655 8.6947 0 9.47737 0 10.4495V16.9009C0 17.8731 0.782655 18.6557 1.75483 18.6557C2.72699 18.6557 3.50967 17.8731 3.50967 16.9009V10.4495C3.50967 9.47737 2.72699 8.6947 1.75483 8.6947Z"
                      fill="#F7CA41"
                    />
                  </svg>
                  Unmet Needs Report
                </button>
                <div>
                  {/* <button
                  type="button"
                  className="btn-default flex cursor-pointer px-4 py-2 shadow transition duration-200 hover:bg-blue-600"
                  onClick={toggleGiveAccess}
                >
                  <FaUserPlus className="mr-2 h-4 w-4" />
                  Give Access
                </button> */}

                  <DialogComponent
                    visible={showPopup}
                    width="480px"
                    isModal={true}
                    closeOnEscape={true}
                    header="Give Access"
                    footerTemplate={footerTemplate}
                    target={document.body}
                    close={onDialogClose}
                    cssClass="e-grid-dialog"
                  >
                    <div className="flex flex-col px-4 py-2">
                      <div className="flex">
                        <div className="my-auto mr-4 font-semibold text-[#242424]">
                          I give permission for
                        </div>
                        <input
                          className="input-style grow rounded-xl bg-zinc-50 py-2.5 pl-3.5 pr-2.5 text-zinc-500"
                          placeholder="Name of Person Receiving"
                        />
                      </div>
                      <div className="mt-5 flex">
                        <div className="my-auto grow font-semibold text-[#242424]">
                          from
                        </div>
                        <input
                          className="input-style grow rounded-xl bg-zinc-50 py-2.5 pl-3.5 pr-2.5 text-zinc-500"
                          placeholder="Organization"
                        />
                      </div>
                      <div className="mt-5 font-semibold text-[#242424]">
                        access to (until my recovery plan is closed):
                      </div>
                      <ul>
                        <li className="mb-3 flex items-center">
                          <input type="checkbox" id="item1" className="mr-2" />
                          <label htmlFor="item1">
                            View and/or update information to my Damage
                            Assessment, Assistance Log, and/or Recovery Plan.
                          </label>
                        </li>
                        <li className="mb-3 flex items-center">
                          <input type="checkbox" id="item2" className="mr-2" />
                          <label htmlFor="item2">
                            View and respond to Case Management Notes made by
                            myself, my case manager, or a nonprofit partner.
                          </label>
                        </li>
                        <li className="mb-3 flex items-center">
                          <input type="checkbox" id="item3" className="mr-2" />
                          <label htmlFor="item3">
                            View My Past Recovery Plans.
                          </label>
                        </li>
                      </ul>
                      <div className="mt-5 flex">
                        <div className="my-auto font-semibold text-neutral-700">
                          Send access code to
                        </div>
                        <input
                          className="input-style grow rounded-xl bg-zinc-50 py-2.5 pl-3.5 pr-2.5 text-zinc-500"
                          placeholder="Organization Email Address"
                        />
                      </div>
                    </div>
                  </DialogComponent>
                </div>
              </div>
            </header>
          </section>

          <section className=" mb-4 box-border h-auto self-stretch overflow-hidden bg-white">
            <HeaderTitle
              id="immediateresponseassistancereceived"
              title="Immediate Response Assistance Received"
            />
            <header
              id="recovery-general"
              className="mb-2 flex flex-row items-center justify-between self-stretch"
            >
              <div id="general" className="flex flex-row items-center gap-4">
                <h2 className="ml-1 text-sm font-semibold text-[#242424]">
                  General
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 font-inter text-xs">
                    <tbody>
                      <tr>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Estimated Repairs:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {generalTotals.totalEstimate.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Donated Goods/Services:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {generalTotals.totalDonated.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Funding Gap:
                        </td>
                        <td
                          className={`w-[140px] border border-gray-300 px-4 py-2 text-right font-semibold ${
                            generalTotals.totalFundingGap < 0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {generalTotals.totalFundingGap.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center bg-white transition-all duration-300 ease-in-out"
                  onClick={toggleGeneral}
                >
                  <span
                    className={`transition-transform duration-300 ease-in-out ${
                      isGeneralVisible ? "rotate-0" : "rotate-180"
                    }`}
                  >
                    <FaChevronUp />
                  </span>
                </button>
              </div>
            </header>
            {isGeneralVisible && (
              <div className="flex flex-col gap-4 self-stretch">
                <GridComponent
                  ref={generalGridRef}
                  allowPdfExport={true}
                  dataSource={generalData}
                  allowSorting={true}
                  sortSettings={sortSettings}
                  editSettings={editing}
                  filterSettings={filterSettings}
                  allowFiltering={false}
                  actionBegin={actionBeginGeneral}
                  actionComplete={actionCompleteGeneral}
                  cssClass="new-grid-color"
                >
                  <ColumnsDirective>
                    <ColumnDirective
                      field="generalID"
                      headerText="generalID"
                      isPrimaryKey={true}
                      visible={false}
                    />
                    <ColumnDirective
                      field="category"
                      headerText="Category"
                      width="200"
                      visible={false}
                      allowEditing={false}
                      defaultValue={
                        categories.find((cat) => cat.categoryId === "1")
                          .categoryName
                      }
                    />
                    <ColumnDirective
                      field="sub_category"
                      headerText="Category"
                      template={categoryTemplate}
                      width="250"
                      editType="dropdownedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          dataSource: subcategories
                            .filter((sub) => sub.categoryId === "1")
                            .map((sub) => sub.subcategoryName),
                          fields: {
                            text: "subcategoryName",
                            value: "subcategoryName",
                          },
                          query: new Query(),
                        },
                      }}
                    />
                    <ColumnDirective
                      field="damage"
                      headerText="Description"
                      width="300"
                      allowEditing={false}
                    />
                    {/* <ColumnDirective
                    field="dateEntered"
                    headerText="Date Entered"
                    width="180"
                    textAlign="Left"
                    allowEditing={false}
                  /> */}
                    <ColumnDirective
                      field="photo_general"
                      headerText="Photo Attachment"
                      width="200"
                      template={gridTemplate}
                      allowEditing={false}
                      visible={false}
                    />
                    <ColumnDirective
                      field="estimate"
                      headerText="Estimate to Repair"
                      width="200"
                      format="C"
                      textAlign="Right"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.estimate).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field="value"
                      headerText="Donated Goods/Services"
                      width="200"
                      format="C"
                      editType="numericedit"
                      textAlign="Right"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.value).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />

                    <ColumnDirective
                      field="fundingGap"
                      headerText="Funding Gap"
                      width="200"
                      textAlign="Right"
                      allowFiltering={false}
                      format="C"
                      allowEditing={false}
                      template={fundingGapTemplate}
                    />

                    <ColumnDirective width="25" />
                  </ColumnsDirective>
                  <Inject
                    services={[
                      Page,
                      Edit,
                      Toolbar,
                      CommandColumn,
                      Sort,
                      Filter,
                      DetailRow,
                      PdfExport,
                    ]}
                  />
                </GridComponent>
              </div>
            )}
          </section>

          <section className=" mb-4 box-border h-auto self-stretch overflow-hidden bg-white">
            <HeaderTitle
              id="recoveryassistancereceived"
              title="Recovery Assistance Received"
            />
            <header
              id="recovery-home-repair"
              className="mb-2 flex flex-row items-center justify-between self-stretch"
            >
              <div
                id="homerepairandreplacement"
                className="flex flex-row items-center gap-4"
              >
                <h2 className="ml-1 text-sm font-semibold text-[#242424]">
                  Home Repair & Replacement
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 font-inter text-xs">
                    <tbody>
                      <tr>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Estimated Repairs:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {homeRepairTotals.totalEstimate.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Donated Goods/Services:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {homeRepairTotals.totalDonated.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Funding Gap:
                        </td>
                        <td
                          className={`w-[140px] border border-gray-300 px-4 py-2 text-right font-semibold ${
                            homeRepairTotals.totalFundingGap < 0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {homeRepairTotals.totalFundingGap.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center bg-white transition-all duration-300 ease-in-out"
                  onClick={toggleHomeRepair}
                >
                  <span
                    className={`transition-transform duration-300 ease-in-out ${
                      isHomeRepairVisible ? "rotate-0" : "rotate-180"
                    }`}
                  >
                    <FaChevronUp />
                  </span>
                </button>
              </div>
            </header>
            {isHomeRepairVisible && (
              <div className="flex flex-col gap-4 self-stretch">
                <GridComponent
                  ref={homeRepairGridRef}
                  allowPdfExport={true}
                  dataSource={homeRepairData}
                  allowSorting={true}
                  sortSettings={sortSettings}
                  editSettings={editing}
                  filterSettings={filterSettings}
                  allowFiltering={false}
                  actionBegin={actionBeginHomeRepair}
                  actionComplete={actionCompleteHomeRepair}
                  cssClass="new-grid-color"
                >
                  <ColumnsDirective>
                    <ColumnDirective
                      field="homeRepairID"
                      headerText="homeRepairID"
                      isPrimaryKey={true}
                      visible={false}
                    />
                    <ColumnDirective
                      field="category"
                      headerText="Category"
                      width="200"
                      visible={false}
                      defaultValue={
                        categories.find((cat) => cat.categoryId === "2")
                          .categoryName
                      }
                    />
                    <ColumnDirective
                      field="sub_category"
                      headerText="Category"
                      template={categoryTemplate}
                      width="250"
                      editType="dropdownedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          dataSource: subcategories
                            .filter((sub) => sub.categoryId === "2")
                            .map((sub) => sub.subcategoryName),
                          fields: {
                            text: "subcategoryName",
                            value: "subcategoryName",
                          },
                          query: new Query(),
                        },
                      }}
                    />
                    <ColumnDirective
                      field="damage"
                      headerText="Description"
                      width="300"
                      allowEditing={false}
                    />
                    {/* <ColumnDirective
                    field="dateEntered"
                    headerText="Date Entered"
                    width="180"
                    textAlign="Left"
                    allowEditing={false}
                  /> */}
                    <ColumnDirective
                      field="photo_home_repair"
                      headerText="Photo Attachment"
                      width="200"
                      template={homeRepairTemplate}
                      allowEditing={false}
                      visible={false}
                    />
                    <ColumnDirective
                      field="estimate"
                      headerText="Estimate to Repair"
                      width="200"
                      format="C"
                      textAlign="Right"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.estimate).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field="value"
                      headerText="Donated Goods/Services"
                      width="200"
                      format="C"
                      textAlign="Right"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.value).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />

                    <ColumnDirective
                      field="fundingGap"
                      headerText="Funding Gap"
                      width="200"
                      textAlign="Right"
                      allowFiltering={false}
                      format="C"
                      allowEditing={false}
                      template={fundingGapTemplate}
                    />
                    <ColumnDirective width="25" />
                  </ColumnsDirective>
                  <Inject
                    services={[
                      Page,
                      Edit,
                      Toolbar,
                      CommandColumn,
                      Sort,
                      Filter,
                      DetailRow,
                      PdfExport,
                    ]}
                  />
                </GridComponent>
              </div>
            )}
          </section>

          <section className=" mb-4 box-border h-auto self-stretch overflow-hidden bg-white">
            <header
              id="recovery-landscaping"
              className="mb-2 flex flex-row items-center justify-between self-stretch"
            >
              <div
                id="landscaping"
                className="flex flex-row items-center gap-4"
              >
                <h2 className="ml-1 text-sm font-semibold text-[#242424]">
                  Landscaping
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 font-inter text-xs">
                    <tbody>
                      <tr>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Estimated Repairs:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {landscapingTotals.totalEstimate.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Donated Goods/Services:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {landscapingTotals.totalDonated.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Funding Gap:
                        </td>
                        <td
                          className={`w-[140px] border border-gray-300 px-4 py-2 text-right font-semibold ${
                            landscapingTotals.totalFundingGap < 0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {landscapingTotals.totalFundingGap.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center bg-white transition-all duration-300 ease-in-out"
                  onClick={toggleLandscaping}
                >
                  <span
                    className={`transition-transform duration-300 ease-in-out ${
                      isLandscapingVisible ? "rotate-0" : "rotate-180"
                    }`}
                  >
                    <FaChevronUp />
                  </span>
                </button>
              </div>
            </header>

            {isLandscapingVisible && (
              <div className="flex flex-col gap-4 self-stretch">
                <GridComponent
                  ref={landscapingGridRef}
                  allowPdfExport={true}
                  dataSource={landscapingData}
                  allowSorting={true}
                  sortSettings={sortSettings}
                  editSettings={editing}
                  filterSettings={filterSettings}
                  allowFiltering={false}
                  actionBegin={actionBeginLandscaping}
                  actionComplete={actionCompleteLandscaping}
                  cssClass="new-grid-color"
                >
                  <ColumnsDirective>
                    <ColumnDirective
                      field="landscapingID"
                      headerText="landscapingID"
                      isPrimaryKey={true}
                      visible={false}
                    />
                    <ColumnDirective
                      field="category"
                      headerText="Category"
                      width="200"
                      visible={false}
                      defaultValue={
                        categories.find((cat) => cat.categoryId === "3")
                          .categoryName
                      }
                      allowEditing={false}
                    />
                    <ColumnDirective
                      field="sub_category"
                      headerText="Category"
                      template={categoryTemplate}
                      width="250"
                      editType="dropdownedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          dataSource: subcategories
                            .filter((sub) => sub.categoryId === "3")
                            .map((sub) => sub.subcategoryName),
                          fields: {
                            text: "subcategoryName",
                            value: "subcategoryName",
                          },
                          query: new Query(),
                        },
                      }}
                    />
                    <ColumnDirective
                      field="damage"
                      headerText="Description"
                      width="300"
                      allowEditing={false}
                    />
                    {/* <ColumnDirective
                    field="dateEntered"
                    headerText="Date Entered"
                    width="180"
                    textAlign="Left"
                    allowEditing={false}
                  /> */}
                    <ColumnDirective
                      field="photo_landscaping"
                      headerText="Photo Attachment"
                      width="200"
                      template={landscapingTemplate}
                      allowEditing={false}
                      visible={false}
                    />
                    <ColumnDirective
                      field="estimate"
                      headerText="Estimate to Repair"
                      width="200"
                      textAlign="Right"
                      format="C"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.estimate).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field="value"
                      headerText="Donated Goods/Services"
                      width="200"
                      textAlign="Right"
                      format="C"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.value).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />

                    <ColumnDirective
                      field="fundingGap"
                      headerText="Funding Gap"
                      width="200"
                      textAlign="Right"
                      allowFiltering={false}
                      format="C"
                      allowEditing={false}
                      template={fundingGapTemplate}
                    />
                    <ColumnDirective width="25" />
                  </ColumnsDirective>
                  <Inject
                    services={[
                      Page,
                      Edit,
                      Toolbar,
                      CommandColumn,
                      Sort,
                      Filter,
                      DetailRow,
                      PdfExport,
                    ]}
                  />
                </GridComponent>
              </div>
            )}
          </section>

          <section className=" mb-4 box-border h-auto self-stretch overflow-hidden bg-white">
            <header
              id="recovery-vehicles"
              className="mb-2 flex flex-row items-center justify-between self-stretch"
            >
              <div id="vehicles" className="flex flex-row items-center gap-4">
                <h2 className="ml-1 text-sm font-semibold text-[#242424]">
                  Vehicle(s)
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 font-inter text-xs">
                    <tbody>
                      <tr>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Estimated Repairs:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {vehicleTotals.totalEstimate.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Donated Goods/Services:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {vehicleTotals.totalDonated.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Funding Gap:
                        </td>
                        <td
                          className={`w-[140px] border border-gray-300 px-4 py-2 text-right font-semibold ${
                            vehicleTotals.totalFundingGap < 0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {vehicleTotals.totalFundingGap.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center bg-white transition-all duration-300 ease-in-out"
                  onClick={toggleVehicle}
                >
                  <span
                    className={`transition-transform duration-300 ease-in-out ${
                      isVehicleVisible ? "rotate-0" : "rotate-180"
                    }`}
                  >
                    <FaChevronUp />
                  </span>
                </button>
              </div>
            </header>

            {isVehicleVisible && (
              <div className="flex flex-col gap-4 self-stretch">
                <GridComponent
                  ref={vehicleGridRef}
                  allowPdfExport={true}
                  dataSource={vehicleData}
                  allowSorting={true}
                  sortSettings={sortSettings}
                  editSettings={editing}
                  filterSettings={filterSettings}
                  allowFiltering={false}
                  actionBegin={actionBeginVehicle}
                  actionComplete={actionCompleteVehicle}
                  cssClass="new-grid-color"
                >
                  <ColumnsDirective>
                    <ColumnDirective
                      field="vehicleID"
                      headerText="vehicleID"
                      isPrimaryKey={true}
                      visible={false}
                    />
                    <ColumnDirective
                      field="category"
                      headerText="Category"
                      width="120"
                      visible={false}
                      allowEditing={false}
                      defaultValue={
                        categories.find((cat) => cat.categoryId === "4")
                          .categoryName
                      }
                    />
                    <ColumnDirective
                      field="sub_category"
                      headerText="Category"
                      allowEditing={false}
                      template={categoryTemplate}
                      width="250"
                      editType="dropdownedit"
                      edit={{
                        params: {
                          dataSource: subcategories
                            .filter((sub) => sub.categoryId === "4")
                            .map((sub) => sub.subcategoryName),
                          fields: {
                            text: "subcategoryName",
                            value: "subcategoryName",
                          },
                          query: new Query(),
                        },
                      }}
                    />
                    <ColumnDirective
                      field="damage"
                      headerText="Description"
                      width="300"
                      allowEditing={false}
                    />
                    {/* <ColumnDirective
                    field="dateEntered"
                    headerText="Date Entered"
                    width="180"
                    textAlign="Left"
                    allowEditing={false}
                  /> */}
                    <ColumnDirective
                      field="photo_vehicle"
                      headerText="Photo Attachment"
                      width="200"
                      template={vehicleTemplate}
                      allowEditing={false}
                      visible={false}
                    />
                    <ColumnDirective
                      field="estimate"
                      headerText="Estimate to Repair"
                      width="200"
                      format="C"
                      textAlign="Right"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.estimate).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field="value"
                      headerText="Donated Goods/Services"
                      width="200"
                      textAlign="Right"
                      format="C"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.value).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />

                    <ColumnDirective
                      field="fundingGap"
                      headerText="Funding Gap"
                      textAlign="Right"
                      width="200"
                      allowFiltering={false}
                      format="C"
                      allowEditing={false}
                      template={fundingGapTemplate}
                    />
                    <ColumnDirective width="25" />
                  </ColumnsDirective>
                  <Inject
                    services={[
                      Page,
                      Edit,
                      Toolbar,
                      CommandColumn,
                      Sort,
                      Filter,
                      DetailRow,
                      PdfExport,
                    ]}
                  />
                </GridComponent>
              </div>
            )}
          </section>

          <section className=" mb-4 box-border h-auto self-stretch overflow-hidden bg-white">
            <header
              id="recovery-contents"
              className="mb-2 flex flex-row items-center justify-between self-stretch"
            >
              <div id="contents" className="flex flex-row items-center gap-4">
                <h2 className="ml-1 text-sm font-semibold text-[#242424]">
                  Contents
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 font-inter text-xs">
                    <tbody>
                      <tr>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Estimated Repairs:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {contentsTotals.totalEstimate.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Donated Goods/Services:
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 text-right text-[#242424]">
                          {contentsTotals.totalDonated.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </td>
                        <td className="w-[140px] border border-gray-300 px-4 py-2 font-semibold text-[#242424]">
                          Funding Gap:
                        </td>
                        <td
                          className={`w-[140px] border border-gray-300 px-4 py-2 text-right font-semibold ${
                            contentsTotals.totalFundingGap < 0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {contentsTotals.totalFundingGap.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD",
                            }
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  className="flex h-8 w-8 cursor-pointer items-center justify-center bg-white transition-all duration-300 ease-in-out"
                  onClick={toggleContents}
                >
                  <span
                    className={`transition-transform duration-300 ease-in-out ${
                      isContentsVisible ? "rotate-0" : "rotate-180"
                    }`}
                  >
                    <FaChevronUp />
                  </span>
                </button>
              </div>
            </header>
            {isContentsVisible && (
              <div className="flex flex-col gap-4 self-stretch">
                <GridComponent
                  ref={contentsGridRef}
                  allowPdfExport={true}
                  dataSource={contentsData}
                  allowSorting={true}
                  sortSettings={sortSettings}
                  editSettings={editing}
                  filterSettings={filterSettings}
                  allowFiltering={false}
                  actionBegin={actionBeginContents}
                  actionComplete={actionCompleteContents}
                  cssClass="new-grid-color"
                >
                  <ColumnsDirective>
                    <ColumnDirective
                      field="contentsID"
                      headerText="contentsID"
                      isPrimaryKey={true}
                      visible={false}
                    />
                    <ColumnDirective
                      field="category"
                      headerText="Category"
                      allowEditing={false}
                      width="120"
                      visible={false}
                      defaultValue={
                        categories.find((cat) => cat.categoryId === "5")
                          .categoryName
                      }
                    />
                    <ColumnDirective
                      field="sub_category"
                      headerText="Category"
                      template={categoryTemplate}
                      width="250"
                      editType="dropdownedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          dataSource: subcategories
                            .filter((sub) => sub.categoryId === "5")
                            .map((sub) => sub.subcategoryName),
                          fields: {
                            text: "subcategoryName",
                            value: "subcategoryName",
                          },
                          query: new Query(),
                        },
                      }}
                    />
                    <ColumnDirective
                      field="damage"
                      headerText="Description"
                      width="300"
                      allowEditing={false}
                    />
                    {/* <ColumnDirective
                    field="dateEntered"
                    headerText="Date Entered"
                    width="180"
                    textAlign="Left"
                    allowEditing={false}
                  /> */}
                    <ColumnDirective
                      field="photo_contents"
                      headerText="Photo Attachment"
                      width="200"
                      template={contentsTemplate}
                      allowEditing={false}
                      visible={false}
                    />
                    <ColumnDirective
                      field="estimate"
                      headerText="Estimate to Repair"
                      width="200"
                      format="C"
                      textAlign="Right"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.estimate).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />
                    <ColumnDirective
                      field="value"
                      headerText="Donated Goods/Services"
                      width="200"
                      format="C"
                      textAlign="Right"
                      editType="numericedit"
                      allowEditing={false}
                      edit={{
                        params: {
                          validateDecimalOnType: true,
                          decimals: 0,
                          format: "C",
                          showSpinButton: false,
                        },
                      }}
                      template={(props) => (
                        <div >
                          ${Number(props.value).toLocaleString("en-US")}{" "}
                          {/* Formats the number with commas */}
                        </div>
                      )}
                    />

                    <ColumnDirective
                      field="fundingGap"
                      headerText="Funding Gap"
                      width="200"
                      textAlign="Right"
                      allowFiltering={false}
                      format="C"
                      allowEditing={false}
                      template={fundingGapTemplate}
                    />
                    <ColumnDirective width="25" />
                  </ColumnsDirective>
                  <Inject
                    services={[
                      Page,
                      Edit,
                      Toolbar,
                      CommandColumn,
                      Sort,
                      Filter,
                      DetailRow,
                      PdfExport,
                    ]}
                  />
                </GridComponent>
              </div>
            )}
          </section>
          <ImageDialog
            isOpen={isImageDialogOpen}
            onClose={() => setIsImageDialogOpen(false)}
            images={selectedImages}
            currentIndex={currentImageIndex}
            setCurrentIndex={setCurrentImageIndex}
          />
        </div>
      </div>
      <FileDialog
        isOpen={isPdfDialogOpen}
        onClose={() => setIsPdfDialogOpen(false)}
        fileUrl={pdfUrl}
      />
    </>
  );
};

export default RecoveryPlanDisasters;
